import { Fragment, useId } from 'react';
import type { ViewFilterNode } from '../../../data/client';
import { Button, Checkbox, cx, Icon, SegmentedControl } from '../../../ui';
import { FilterBuilder } from '../../filters/FilterBuilder';
import { describeViewFilter, type RelationContext } from '../../filters/filterModel';
import type { SortState, TableColumn } from './tableModel';
import type { GroupingControls } from './useTableControls';

/**
 * THE TABLE'S CONTROLS AS SHEET SUB-PAGES (2026-07-30, after Arif opened the Adjust sheet
 * on his own phone).
 *
 * What he found: Filter, Sort and Columns opened DESKTOP POPOVERS floating over the sheet.
 * The columns dropdown ran nearly the full height of the screen and the filter editor
 * crammed its option checkboxes into a 300px panel. An iOS app does not float a menu over
 * a sheet - it DRILLS IN, and the row that drills in carries a chevron and its current
 * value (HIG Navigation, sheets and disclosure rows).
 *
 * So the three rows push these pages inside the same open sheet, through `SheetNavigator`.
 * The list behind the sheet keeps reflecting every change as it is made, which is why
 * none of them has a Done button: the only page that COMMITS rather than applying live is
 * the filter, because each apply is a server query.
 *
 * The desktop popovers are untouched. This is a second PLACEMENT of the same state, not a
 * second set of semantics - and the one piece with real logic in it, the filter builder,
 * is literally the same component in both.
 */
export type TableSheetPageId = 'filter' | 'sort' | 'columns' | 'group';

/**
 * What every placement of the table's controls reads and writes. Derived once by
 * `useTableControls` and handed to the desktop toolbar, the sheet's rows and these pages,
 * so "which columns are shown" is answered in one place for all three.
 */
export interface TableControlProps {
  readonly objectId: string;
  /**
   * D27's anchor set plus the fields behind it (D21). It travels with the other controls
   * rather than being read here, because the two PLACEMENTS of this editor - the desktop
   * toolbar and the sheet's sub-page - must not issue the read twice or disagree about it.
   */
  readonly relations: RelationContext;
  readonly columns: readonly TableColumn[];
  /** Which columns are SHOWN. Derived by the caller from the view's column list. */
  readonly shownFieldIds: readonly string[];
  readonly onToggleColumn: (fieldId: string, visible: boolean) => void;
  readonly sort: SortState | null;
  readonly onSortChange: (next: SortState | null) => void;
  /**
   * The applied filter in its STORED form (D20). It carried only `onFilterChange`
   * before, which made the toolbar uncontrolled in the one direction that matters:
   * loading a saved view changed the list and left this button saying "Filter".
   */
  readonly filter: ViewFilterNode | null;
  readonly onFilterChange: (filter: ViewFilterNode | null) => void;
  /**
   * The grouped table's four settings (group-by-aggregation.md D15, D18, D29). One member
   * rather than eight props, because they are one surface: the Group button on the desktop
   * and the Group page in the sheet are two PLACEMENTS of this object.
   */
  readonly grouping: GroupingControls;
}

/**
 * The product's section-label vocabulary (Card, CollapsibleSection, DropdownLabel):
 * 13px micro-caps in muted ink. The explaining SENTENCE belongs to the sheet's own
 * description, not to four labels shouting a paragraph each.
 */
export const SHEET_LABEL = 'text-2xs font-semibold tracking-widest text-ink-muted uppercase';

/**
 * A settings row's own height, and the hairline that separates it from the next.
 *
 * THE HAIRLINE IS A PSEUDO-ELEMENT AND NOT A BORDER (E5, 2026-09-07). `.touch-target` is
 * `min-height: var(--touch-target)`, 44px, and a `border-b` lives INSIDE that box - so the
 * row's padding box was 43px and the `inset-0` input that makes the whole strip pressable
 * was 43px with it. R5 reported exactly that at 375 once the phone floor became 44. A
 * drawn line under the row costs the row nothing, and the target is the full 44.
 */
const SHEET_ROW =
  'touch-target relative flex items-center after:pointer-events-none after:absolute ' +
  'after:inset-x-0 after:bottom-0 after:h-px after:bg-line-subtle after:content-[""] ' +
  'last:after:hidden';

/* --- what the rows say without being opened -------------------------------- */

/**
 * "None" rather than the empty string, in all three. A disclosure row whose trailing slot
 * is blank reads as a value that failed to load; iOS writes "Off" there and means the
 * same thing. Filter deliberately does NOT reuse `describeViewFilter`'s null answer,
 * which is the word "Filter" - a row labelled "Filter" with the value "Filter" is the
 * kind of thing only a computer writes.
 */
export function describeFilterValue(
  filter: ViewFilterNode | null,
  columns: readonly TableColumn[],
  relations: RelationContext,
): string {
  return filter === null
    ? 'None'
    : describeViewFilter(
        filter,
        columns.map((column) => column.field),
        relations,
      );
}

export function describeSortValue(sort: SortState | null, columns: readonly TableColumn[]): string {
  if (sort === null) return 'None';
  const column = columns.find((entry) => entry.field.id === sort.fieldId);
  if (column === undefined) return 'None';
  return `${column.field.label}, ${sort.direction === 'asc' ? 'ascending' : 'descending'}`;
}

export function describeColumnsValue(
  shownFieldIds: readonly string[],
  columns: readonly TableColumn[],
): string {
  return `${String(shownFieldIds.length)} of ${String(columns.length)} shown`;
}

/* --- the pages ------------------------------------------------------------- */

/**
 * SORT: one field per row, radio semantics, a checkmark on the active one.
 *
 * REAL RADIOS, for the reason SegmentedControl gives: the platform's keyboard model
 * (one tab stop, arrows move and select) instead of one re-implemented on div soup, and a
 * group a screen reader already knows how to announce. The ROW is the label and the input
 * is stretched over it, so the whole 44px strip is the target and the focus ring is the
 * row's own outline.
 *
 * PICKING A FIELD STARTS IT ASCENDING, unchanged from the desktop menu. What is NOT here
 * is that menu's "tap the active field again to flip" trick, and it cannot be: a checked
 * radio fires no change event, so the row would be dead rather than clever. The direction
 * control below is the explicit affordance, which is the one the desktop menu already
 * carries for people who never learned the trick.
 */
export function SortSubPage({ columns, sort, onSortChange }: TableControlProps) {
  const name = useId();

  return (
    <div className="space-y-5">
      <fieldset>
        <legend className="sr-only">Sort by</legend>
        {columns.map((column) => {
          const active = sort?.fieldId === column.field.id;
          return (
            <label
              key={column.field.id}
              /* THE ARROW, NEVER THE HAND (wave 4, prep section 0, and the owner's cursor
                 decision). The hand is the web's mark for a LINK; macOS shows the arrow over
                 a control, and an option row in a sheet is a control. `cursor-pointer` stood
                 on the label and again on the input that covers it, so both are gone. */
              className={cx(SHEET_ROW, 'gap-3 rounded-control px-3')}
            >
              {/* The input COVERS the row rather than hiding beside it, which is the trick
                  SegmentedControl uses for the same reason twice over: the whole 44px strip
                  is the target, and the global focus-visible ring lands on the row outline
                  instead of on a 1px box somewhere inside it. */}
              <input
                type="radio"
                name={name}
                className="absolute inset-0 m-0 appearance-none rounded-control"
                checked={active}
                onChange={() => onSortChange({ fieldId: column.field.id, direction: 'asc' })}
              />
              <span className="pointer-events-none min-w-0 flex-1 truncate text-base text-ink">
                {column.field.label}
              </span>
              {active && <Icon name="check" className="pointer-events-none shrink-0 text-accent" />}
            </label>
          );
        })}
      </fieldset>

      {sort !== null && (
        <section className="flex flex-col gap-2">
          <h4 className={SHEET_LABEL}>Direction</h4>
          <SegmentedControl
            className="w-full"
            value={sort.direction}
            onValueChange={(direction) => onSortChange({ ...sort, direction })}
            ariaLabel="Direction"
            segments={[
              { value: 'asc', label: 'Ascending' },
              { value: 'desc', label: 'Descending' },
            ]}
          />
          <Button variant="ghost" block onClick={() => onSortChange(null)}>
            Clear sort
          </Button>
        </section>
      )}
    </div>
  );
}

/**
 * COLUMNS: a checkbox per column at the touch floor.
 *
 * The title column is RENDERED and disabled rather than left out - a list that silently
 * omits the field somebody is looking for teaches nothing, and the caption under it is
 * one line explaining the one row that cannot be switched. It sits directly beneath that
 * row instead of under the whole list: on an object with twenty fields, a footnote is a
 * scroll away from the thing it is about.
 */
export function ColumnsSubPage({ columns, shownFieldIds, onToggleColumn }: TableControlProps) {
  return (
    <div>
      {columns.map((column) => (
        <Fragment key={column.field.id}>
          <div className={SHEET_ROW}>
            <Checkbox
              className="w-full"
              // A row with no title is unusable, so this one is shown and refused.
              disabled={column.isTitle}
              checked={column.isTitle || shownFieldIds.includes(column.field.id)}
              onCheckedChange={(visible) => onToggleColumn(column.field.id, visible)}
              label={column.field.label}
            />
          </div>
          {column.isTitle && (
            <p className="px-1 pt-1 pb-2 text-sm text-ink-muted">
              The title column cannot be hidden.
            </p>
          )}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * FILTER: the builder as block content. The same component the desktop popover renders,
 * laid out for a full-width sheet - condition cards fill the row and an option set is a
 * list of rows rather than a column of checkboxes squeezed into 300px.
 *
 * Applying or clearing returns to the root of the sheet, which is what the popover's
 * closing does on a desktop. The sheet itself stays open either way.
 */
export function FilterSubPage({
  objectId,
  columns,
  filter,
  onFilterChange,
  onDone,
}: TableControlProps & { readonly onDone: () => void }) {
  return (
    <FilterBuilder
      layout="block"
      objectId={objectId}
      fields={columns.map((column) => column.field)}
      value={filter}
      onChange={onFilterChange}
      onDone={onDone}
    />
  );
}
